import { useEffect, useMemo, useState } from 'react';
import { msUntilNextMoscowMidnight, pickNextPoolGiantToPreload } from '../../../domain';
import { preloadSessionLevelAssets } from '../../assets/preloadSessionLevelAssets';
import { useAppFit } from '../../hooks/useAppFit';
import { useAppStore } from '../../store/appStore';
import { getCurrentCourierLicenseLabel } from '../player_level_screen/courierLicenseLabels';
import {
  clampCamera,
  createInitialCamera,
  createMapScreenModel,
  type MapScreenProps,
} from './MapScreenModelScreen';
import { MapScreenController } from './MapScreenController';
import { MapScreenView } from './MapScreenView';

export type { MapScreenProps } from './MapScreenModelScreen';

export function MapScreen(props: MapScreenProps = {}) {
  useEffect(() => {
    void useAppStore.getState().refreshEconomy();
  }, []);

  const deliveryPool = useAppStore((s) => s.playerEconomy.deliveryPool);

  useEffect(() => {
    const next = pickNextPoolGiantToPreload(deliveryPool, null);
    if (next == null) {
      return;
    }
    void preloadSessionLevelAssets([next], { limit: 1 });
  }, [deliveryPool]);

  useEffect(() => {
    function scheduleMidnightRefresh(): number {
      const msUntilMidnight = Math.max(1, msUntilNextMoscowMidnight(Date.now()));
      return window.setTimeout(() => {
        void useAppStore.getState().refreshEconomy();
        timerId = scheduleMidnightRefresh();
      }, msUntilMidnight);
    }

    let timerId = scheduleMidnightRefresh();
    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  const successfulDeliveries = useAppStore((s) => s.playerEconomy.ratedDeliveriesCount);
  const averageRating = useAppStore((s) => s.playerEconomy.totalRating);
  const lastRatingDelta = useAppStore((s) => s.lastRatingDelta);
  const playerLevelName = useMemo(
    () => getCurrentCourierLicenseLabel(successfulDeliveries, averageRating),
    [successfulDeliveries, averageRating],
  );

  const fit = useAppFit();
  const viewWidth = fit.size.width;
  const viewHeight = fit.size.height;
  const [camera, setCamera] = useState(() =>
    createInitialCamera({ width: viewWidth, height: viewHeight }),
  );

  useEffect(() => {
    setCamera((prev) => clampCamera(prev, { width: viewWidth, height: viewHeight }));
  }, [viewWidth, viewHeight]);

  const model = createMapScreenModel(
    { ...props, playerLevelName, averageRating, lastRatingDelta },
    { camera, setCamera, viewSize: fit.size, fitScale: fit.scale },
  );
  const controller = new MapScreenController(model);

  return <MapScreenView model={model} controller={controller} />;
}
